import React, { Suspense, lazy, useState } from 'react';
import styled from 'styled-components';

import type { FieldType } from '@lt/widget/components/Cigarette/types';

import { SEARCH_TYPE_NAMES } from 'src/constants/search';

import ErrorBoundary from 'src/error/components/ErrorBoundary';
import CigaretteSkeleton from 'src/components/CigaretteSkeleton';

import type { PopupComponentProps } from 'src/components/TouristLimitModal/types';
import TouristLimitModal from 'src/components/TouristLimitModal';
import { useTouristsLimitCount } from 'src/components/Cigarette/components/LazyCigarette/useTouristsLimitCount';

import DesktopCigarettePreview from '../DesktopCigarettePreview';
import WizardCigarettePreview from '../WizardCigarettePreview';

import type { ILazyCigarette } from './types';
import { CIGARETTE_FILTER_TEST_ID } from './constants';

const Cigarette = lazy(
  () => import(/* webpackChunkName: "Cigarette" */ 'src/components/Cigarette'),
);

export const LazyCigarette = (props: ILazyCigarette) => {
  const {
    isMobile = false,
    searchType,
    cigaretteValues,
    isMainPage = false,
    className = '',
    setIsWizardOpen,
    setBodyLocked,
    isWizardOpen = false,
    isWhiteLabel = false,
    trackSubmit,
    variant = 'default',
    onSubmit,
    allowPreview = true,
    inHeader = false,
    initialOpenedField,
  } = props;

  const [activeField, setActiveField] = useState<FieldType | undefined>(
    initialOpenedField,
  );

  const { tourists } = cigaretteValues || {};

  const {
    adultsValue,
    kidsValue,
    showTourisLimitPopup,
    setShowTourisLimitPopup,
  } = useTouristsLimitCount(searchType, tourists);

  const showPreview = allowPreview && !activeField;

  const PreviewComponent = isMobile
    ? WizardCigarettePreview
    : DesktopCigarettePreview;

  const mobileVariant =
    (isMobile && isMainPage && 'wizard') ||
    (isMobile && 'default') ||
    undefined;

  const preparedCigaretteValues =
    searchType === SEARCH_TYPE_NAMES.TRIP
      ? undefined
      : {
          ...cigaretteValues,
          tourists: {
            adults: adultsValue,
            kids: kidsValue,
          },
        };

  const loadCigarette = (clickedPreviewField: FieldType) => {
    setActiveField(clickedPreviewField);
  };

  const renderPopupComponent = (popupProps: PopupComponentProps) => (
    <TouristLimitModal {...popupProps} />
  );

  return showPreview ? (
    <PreviewComponent
      onFieldClick={loadCigarette}
      cigaretteValues={preparedCigaretteValues}
      searchType={searchType}
      variant={variant}
      data-testid={CIGARETTE_FILTER_TEST_ID}
      showTourisLimitPopup={showTourisLimitPopup}
      setShowTourisLimitPopup={setShowTourisLimitPopup}
    />
  ) : (
    <ErrorBoundary showDialog={false}>
      <Suspense
        fallback={
          <CigaretteSkeleton hasMaxWidth={!inHeader} hasButton={!isMobile} />
        }
      >
        <StyledCigarette
          className={className}
          isMainPage={isMainPage}
          variant={variant}
          mobileVariant={mobileVariant}
          searchType={searchType}
          trackSubmit={trackSubmit}
          setIsWizardOpen={setIsWizardOpen}
          $isWizardOpen={isWizardOpen}
          initialValues={preparedCigaretteValues}
          initialOpenedField={activeField}
          onSubmit={onSubmit}
          isWhiteLabel={isWhiteLabel}
          setBodyLocked={setBodyLocked}
          data-testid={CIGARETTE_FILTER_TEST_ID}
          renderPopupComponent={renderPopupComponent}
        />
      </Suspense>
    </ErrorBoundary>
  );
};

const StyledCigarette = styled(Cigarette)<{ $isWizardOpen?: boolean }>`
  ${({ $isWizardOpen, mobileVariant }) =>
    $isWizardOpen &&
    mobileVariant &&
    `
      position: fixed;
      left: 0;
    `}
`;
